import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDigestConfigs, useDigestRuns, useDiffSummary } from '@/hooks/useDigestAdmin';
import { useTeams, type TeamSummary } from '@/hooks/useTeams';
import type { DigestConfig, DigestRunTrigger } from '@/types/digestAdmin';

// Internal teams whose digests we hide by default to keep the cross-account
// view focused on real customers.
const HIDDEN_TEAM_NAMES = new Set(['PricingSaaS', "john's Team"]);

function useTeamFilter<T extends { owner_id: string }>(rows: T[]) {
  const [teamFilter, setTeamFilter] = useState<string>('default');
  const { data: teamMap, isLoading: teamsLoading } = useTeams(
    rows.map((r) => r.owner_id)
  );

  const visibleRows = useMemo(() => {
    if (teamFilter === 'all') return rows;
    // While teams are loading, return empty so default filter doesn't flicker
    // a full unfiltered list before pruning. List view shows "Loading…" anyway.
    if (!teamMap) return [];
    if (teamFilter === 'default') {
      return rows.filter((r) => {
        const t = teamMap.get(r.owner_id);
        if (!t) return false;
        return !HIDDEN_TEAM_NAMES.has(t.name);
      });
    }
    return rows.filter((r) => r.owner_id === teamFilter);
  }, [rows, teamMap, teamFilter]);

  const teamOptions = useMemo<TeamSummary[]>(() => {
    if (!teamMap) return [];
    return Array.from(teamMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [teamMap]);

  return { teamFilter, setTeamFilter, visibleRows, teamMap, teamOptions, teamsLoading };
}

function TeamFilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (next: string) => void;
  options: TeamSummary[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[240px]">
        <SelectValue placeholder="Team filter" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="default">Customer teams (default)</SelectItem>
        <SelectItem value="all">All teams (incl. internal)</SelectItem>
        {options.map((t) => (
          <SelectItem key={t.id} value={t.id}>
            {t.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Browser TZ abbreviation (e.g. "EST" / "EDT"). Computed once per render — the
// underlying timezone won't change mid-session.
const TZ_ABBR =
  new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(new Date())
    .find((p) => p.type === 'timeZoneName')?.value ?? 'local';

// Converts a "HH:MM UTC, day_of_week N" or "HH:MM UTC, day_of_month N" schedule
// into a label expressed in the browser's local timezone. day-of-week / month
// can shift across the dateline depending on TZ offset.
const TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

// Compute the next time this config will fire (in UTC). Returns null if the
// config doesn't have a schedule (e.g. cadence is realtime-only).
function nextFire(c: DigestConfig): Date | null {
  if (!c.time_of_day) return null;
  const [hh = 0, mm = 0] = c.time_of_day.split(':').map(Number);
  const now = new Date();

  if (c.cadence === 'weekly' && c.day_of_week !== null) {
    const daysAhead = (c.day_of_week - now.getUTCDay() + 7) % 7;
    const candidate = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysAhead, hh, mm)
    );
    if (candidate <= now) candidate.setUTCDate(candidate.getUTCDate() + 7);
    return candidate;
  }
  if (c.cadence === 'monthly' && c.day_of_month !== null) {
    let candidate = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), c.day_of_month, hh, mm)
    );
    if (candidate <= now) {
      candidate = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, c.day_of_month, hh, mm)
      );
    }
    return candidate;
  }
  return null;
}

const DATE_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

function relativeFire(target: Date): string {
  const now = new Date();
  // Compare in *local* calendar days so "Tomorrow" matches the user's wall clock.
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetStart = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const dayDiff = Math.round((targetStart.getTime() - todayStart.getTime()) / 86_400_000);
  const time = TIME_FMT.format(target);

  if (dayDiff === 0) return `Today at ${time} ${TZ_ABBR}`;
  if (dayDiff === 1) return `Tomorrow at ${time} ${TZ_ABBR}`;
  if (dayDiff > 1 && dayDiff < 7) {
    return `In ${dayDiff} days · ${DOW[target.getDay()]} at ${time} ${TZ_ABBR}`;
  }
  return `${DATE_FMT.format(target)} at ${time} ${TZ_ABBR} · in ${dayDiff} days`;
}

function whenLabel(c: DigestConfig): string {
  if (c.cadence === 'weekly' && c.day_of_week !== null && c.time_of_day) {
    const [hh = 0, mm = 0] = c.time_of_day.split(':').map(Number);
    // 2024-01-07 was a Sunday — anchor to it so day_of_week=0 lands correctly.
    const utcDate = new Date(Date.UTC(2024, 0, 7 + c.day_of_week, hh, mm));
    const dow = DOW[utcDate.getDay()];
    return `Every ${dow} at ${TIME_FMT.format(utcDate)} ${TZ_ABBR}`;
  }
  if (c.cadence === 'monthly' && c.day_of_month !== null && c.time_of_day) {
    const [hh = 0, mm = 0] = c.time_of_day.split(':').map(Number);
    const utcDate = new Date(Date.UTC(2024, 0, c.day_of_month, hh, mm));
    return `Monthly on the ${ordinal(utcDate.getDate())} at ${TIME_FMT.format(utcDate)} ${TZ_ABBR}`;
  }
  return '—';
}

// Resolves a run's diff_id into the slug of the company whose published diff
// triggered the alert. Cached per diff_id so duplicate rows share one fetch.
function TriggeringCompanyCell({ diffId }: { diffId: string }) {
  const { data, isLoading } = useDiffSummary(diffId);
  if (isLoading) {
    return <span className="text-xs text-gray-400">…</span>;
  }
  if (!data?.slug) {
    return (
      <span className="font-mono text-xs text-gray-400" title={diffId}>
        {diffId.slice(0, 8)}
      </span>
    );
  }
  return (
    <Link
      to={`/companies/${data.slug}`}
      className="text-xs flex items-center gap-1 hover:underline"
    >
      {data.company_logo && (
        <img src={data.company_logo} alt="" className="w-3 h-3 rounded-sm" />
      )}
      <span>{data.slug}</span>
    </Link>
  );
}

function TeamCell({ ownerId, team }: { ownerId: string; team?: TeamSummary }) {
  if (!team) {
    return (
      <span className="font-mono text-xs text-gray-400" title={ownerId}>
        {ownerId.slice(0, 8)}…
      </span>
    );
  }
  return (
    <div className="flex flex-col">
      <span className="text-sm">{team.name}</span>
      <span className="font-mono text-xs text-gray-400" title={ownerId}>
        {team.slug}
      </span>
    </div>
  );
}

function ScheduledTab() {
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useDigestConfigs({
    search: search || undefined,
    limit: 200,
  });

  // Scheduled = non-realtime cadence (weekly or monthly), regardless of enabled
  const rows = useMemo(
    () =>
      (data?.configs ?? [])
        .filter((c) => c.cadence === 'weekly' || c.cadence === 'monthly')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  );

  const { teamFilter, setTeamFilter, visibleRows, teamMap, teamOptions, teamsLoading } =
    useTeamFilter(rows);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Input
          className="max-w-xs"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <TeamFilterSelect
          value={teamFilter}
          onChange={setTeamFilter}
          options={teamOptions}
        />
      </div>
      {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Team</TableHead>
            <TableHead>When</TableHead>
            <TableHead>Recipients</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(isLoading || teamsLoading) && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-gray-500">
                Loading…
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !teamsLoading && visibleRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-gray-500">
                {rows.length === 0
                  ? 'No scheduled digests.'
                  : 'No digests match the team filter.'}
              </TableCell>
            </TableRow>
          )}
          {visibleRows.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="font-medium">
                <Link to={`/digests/${c.id}`} className="text-blue-600 hover:underline">
                  {c.name}
                </Link>
              </TableCell>
              <TableCell>
                <TeamCell ownerId={c.owner_id} team={teamMap?.get(c.owner_id)} />
              </TableCell>
              <TableCell>
                {(() => {
                  const next = nextFire(c);
                  if (!next) return <span>{whenLabel(c)}</span>;
                  return (
                    <div className="flex flex-col">
                      <span className="text-sm">{relativeFire(next)}</span>
                      <span className="text-xs text-gray-400">{whenLabel(c)}</span>
                    </div>
                  );
                })()}
              </TableCell>
              <TableCell>{c.recipients?.length ?? 0}</TableCell>
              <TableCell>
                <Badge variant={c.enabled ? 'default' : 'secondary'}>
                  {c.enabled ? 'enabled' : 'disabled'}
                </Badge>
              </TableCell>
              <TableCell className="text-xs text-gray-500">
                {format(new Date(c.updated_at), 'yyyy-MM-dd')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RealtimeTab() {
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useDigestConfigs({
    search: search || undefined,
    limit: 200,
  });

  // Realtime = realtime_enabled flag is on
  const rows = useMemo(
    () =>
      (data?.configs ?? [])
        .filter((c) => c.realtime_enabled)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data]
  );

  const { teamFilter, setTeamFilter, visibleRows, teamMap, teamOptions, teamsLoading } =
    useTeamFilter(rows);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Input
          className="max-w-xs"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <TeamFilterSelect
          value={teamFilter}
          onChange={setTeamFilter}
          options={teamOptions}
        />
      </div>
      {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Team</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead>Recipients</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(isLoading || teamsLoading) && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-gray-500">
                Loading…
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !teamsLoading && visibleRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-gray-500">
                {rows.length === 0
                  ? 'No realtime alerts configured.'
                  : 'No alerts match the team filter.'}
              </TableCell>
            </TableRow>
          )}
          {visibleRows.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="font-medium">
                <Link to={`/digests/${c.id}`} className="text-blue-600 hover:underline">
                  {c.name}
                </Link>
              </TableCell>
              <TableCell>
                <TeamCell ownerId={c.owner_id} team={teamMap?.get(c.owner_id)} />
              </TableCell>
              <TableCell>On diff publish</TableCell>
              <TableCell>{c.recipients?.length ?? 0}</TableCell>
              <TableCell>
                <Badge variant={c.enabled ? 'default' : 'secondary'}>
                  {c.enabled ? 'enabled' : 'disabled'}
                </Badge>
              </TableCell>
              <TableCell className="text-xs text-gray-500">
                {format(new Date(c.updated_at), 'yyyy-MM-dd')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SentTab() {
  const [statusFilter, setStatusFilter] = useState<'sent' | 'all'>('sent');
  const [triggerFilter, setTriggerFilter] = useState<'all' | DigestRunTrigger>('all');

  const { data: runsResp, isLoading, error } = useDigestRuns({
    status: statusFilter === 'sent' ? 'sent' : undefined,
    trigger: triggerFilter === 'all' ? undefined : triggerFilter,
    limit: 200,
  });
  const runs = useMemo(() => runsResp?.runs ?? [], [runsResp]);

  // Pull all configs once to map config_id -> human name + cadence.
  const { data: configsResp } = useDigestConfigs({ limit: 500 });
  const configMap = useMemo(() => {
    const m = new Map<string, DigestConfig>();
    for (const c of configsResp?.configs ?? []) m.set(c.id, c);
    return m;
  }, [configsResp]);

  const { teamFilter, setTeamFilter, visibleRows, teamMap, teamOptions, teamsLoading } =
    useTeamFilter(runs);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Select
          value={triggerFilter}
          onValueChange={(v) => setTriggerFilter(v as 'all' | DigestRunTrigger)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Trigger" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All triggers</SelectItem>
            <SelectItem value="weekly">Scheduled (weekly)</SelectItem>
            <SelectItem value="realtime">Realtime alert</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as 'sent' | 'all')}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sent">Only sent</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
          </SelectContent>
        </Select>
        <TeamFilterSelect
          value={teamFilter}
          onChange={setTeamFilter}
          options={teamOptions}
        />
      </div>
      {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>Team</TableHead>
            <TableHead>Digest</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sent</TableHead>
            <TableHead>Period / Diff</TableHead>
            <TableHead>Reason</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(isLoading || teamsLoading) && (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-gray-500">
                Loading…
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !teamsLoading && visibleRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-gray-500">
                {runs.length === 0
                  ? 'No emails recorded for this filter.'
                  : 'No emails match the team filter.'}
              </TableCell>
            </TableRow>
          )}
          {visibleRows.map((r) => {
            const cfg = configMap.get(r.config_id);
            return (
              <TableRow key={r.id}>
                <TableCell className="text-xs whitespace-nowrap">
                  {format(new Date(r.started_at), 'yyyy-MM-dd HH:mm')}
                </TableCell>
                <TableCell>
                  <TeamCell ownerId={r.owner_id} team={teamMap?.get(r.owner_id)} />
                </TableCell>
                <TableCell className="font-medium">
                  <Link
                    to={`/digests/${r.config_id}`}
                    className="text-blue-600 hover:underline"
                  >
                    {cfg?.name ?? r.config_id.slice(0, 8)}
                  </Link>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{r.trigger}</Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      r.status === 'sent'
                        ? 'default'
                        : r.status === 'failed'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {r.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">{r.recipients_sent ?? 0}</TableCell>
                <TableCell>
                  {r.diff_id ? (
                    <TriggeringCompanyCell diffId={r.diff_id} />
                  ) : r.period ? (
                    <span className="font-mono text-xs">{r.period}</span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-gray-600 max-w-xs truncate">
                  {r.skip_reason || r.error || '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function DigestAdmin() {
  return (
    <div className="container mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Digests</CardTitle>
          <p className="text-sm text-gray-500">
            What's scheduled to go out, which configs fire in realtime, and a unified
            log of emails actually sent.
          </p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="scheduled">
            <TabsList>
              <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
              <TabsTrigger value="realtime">Realtime alerts</TabsTrigger>
              <TabsTrigger value="sent">Sent</TabsTrigger>
            </TabsList>
            <TabsContent value="scheduled" className="mt-4">
              <ScheduledTab />
            </TabsContent>
            <TabsContent value="realtime" className="mt-4">
              <RealtimeTab />
            </TabsContent>
            <TabsContent value="sent" className="mt-4">
              <SentTab />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
